import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Play } from 'lucide-react';
import { Label } from '@/components/ui/label';

export interface VideoItem {
  id: string;
  name: string;
  thumbnail: string;
  duration: number;
  views: number;
}

interface VideoSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableVideos: VideoItem[];
  selectedVideoIds: string[];
  onConfirm: (videoIds: string[]) => void;
}

export function VideoSelectModal({
  isOpen,
  onClose,
  availableVideos,
  selectedVideoIds,
  onConfirm,
}: VideoSelectModalProps) {
  useLanguage();
  const [search, setSearch] = useState('');
  const [tempSelected, setTempSelected] = useState<string[]>(selectedVideoIds);

  const filteredVideos = availableVideos.filter((video) =>
    video.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = (videoId: string) => {
    if (tempSelected.includes(videoId)) {
      setTempSelected(tempSelected.filter((id) => id !== videoId));
    } else {
      setTempSelected([...tempSelected, videoId]);
    }
  };

  const handleConfirm = () => {
    onConfirm(tempSelected);
    onClose();
  };

  const handleCancel = () => {
    setTempSelected(selectedVideoIds);
    setSearch('');
    onClose();
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-[600px] w-[95vw] max-w-[95vw] max-h-[85vh] p-0">
        <div className="flex flex-col h-full">
          <DialogHeader className="px-6 py-5 border-b shrink-0">
            <DialogTitle className="text-lg font-semibold">
              {translateInterfaceText("Adicionar Vídeos")}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-1">
              {translateInterfaceText("Selecione os vídeos que deseja adicionar à playlist")}</DialogDescription>
          </DialogHeader>

          <div className="px-6 pt-4 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={translateInterfaceText("Buscar vídeos...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <ScrollArea className="flex-1 px-6 py-4">
            <div className="space-y-3">
              {filteredVideos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>{translateInterfaceText("Nenhum vídeo encontrado")}</p>
                </div>
              ) : (
                filteredVideos.map((video) => {
                  const isSelected = tempSelected.includes(video.id);
                  const isAlreadyInPlaylist = selectedVideoIds.includes(video.id);

                  return (
                    <div
                      key={video.id}
                      className={`flex items-center gap-3 border rounded-[10px] p-3 transition-colors ${
                        isSelected ? 'border-[#2D7BFF] bg-[#E8F1FF]/30' : 'border-[#E6ECF5]'
                      }`}
                    >
                      <Checkbox
                        id={`video-${video.id}`}
                        checked={isSelected}
                        onCheckedChange={() => handleToggle(video.id)}
                        className="data-[state=checked]:bg-[#2D7BFF] data-[state=checked]:border-[#2D7BFF]"
                      />

                      <div className="relative flex-shrink-0 w-24 h-14 overflow-hidden rounded-lg group">
                        <img
                          src={video.thumbnail}
                          alt={video.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play className="h-5 w-5 text-white" />
                        </div>
                        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/80 rounded text-[10px] text-white font-medium">
                          {formatDuration(video.duration)}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <Label
                          htmlFor={`video-${video.id}`}
                          className="cursor-pointer font-medium text-sm line-clamp-2"
                        >
                          {video.name}
                        </Label>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">
                            {video.views.toLocaleString(getInterfaceLanguage())} {" "}{translateInterfaceText("views")}</span>
                          {isAlreadyInPlaylist && (
                            <span className="text-xs text-[#2D7BFF] font-medium">
                              {translateInterfaceText("Na playlist")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="sticky bottom-0 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.05)] px-6 py-3 border-t shrink-0 flex-row gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleCancel} className="flex-1">
              {translateInterfaceText("Cancelar")}</Button>
            <Button
              onClick={handleConfirm}
              className="flex-1 bg-[#2D7BFF] hover:bg-[#1F5ED6] text-white"
              disabled={tempSelected.length === 0}
            >
              {translateInterfaceText("Adicionar")}{" "}{tempSelected.length > 0 && translateInterfaceText("{value0} vídeos", { value0: (tempSelected.length) })}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
